import { AlertType, AlertFocusIdentity } from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PropertyGridCellKey } from '../../components/PropertyGrid/types.ts';
import { Tkv2ReferenceBarProperty } from '../../controller/types.ts';
import { ReferenceViewID, formatReferenceViewID } from '../../modules/referenceView/index.ts';
import { DataSheet } from '../../modules/referenceView/types.ts';
import { ReferenceBarUIState } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { processAlertsForRoute } from './uiSlice.ts';

export const initialReferenceBarUIState: ReferenceBarUIState = {
  referenceViewOpen: false,
  mobileReferenceMenuOpen: false,
  properties: [],
  selectedDataSheet: null,
  alertsPanelAutoOpenedForNavBlock: false,
  focusedAlertType: null,
  focusedAlertPropertyID: null,
  focusedAlertTargetCellKey: null,
  hoveredAlertType: null,
  hoveredAlertPropertyID: null,
  hoveredAlertTargetCellKey: null,
  hoveredPropertyGridCellKey: null,
  hoveredOrderDetailsFieldKey: null,
  hoveredRequiredFieldKey: null,
  hoveredCostApproachPropertyId: null,
  __doNotUseDirectly__ActiveID: formatReferenceViewID({ type: 'subject' }),
};

const referenceBarUISlice = createSlice({
  name: 'referenceBarUI',
  initialState: initialReferenceBarUIState,
  reducers: {
    setReferenceViewOpen(state, action: PayloadAction<boolean>) {
      if (!action.payload) {
        state.selectedDataSheet = null;
        state.focusedAlertType = null;
        state.focusedAlertPropertyID = null;
        state.focusedAlertTargetCellKey = null;
        state.hoveredAlertType = null;
        state.hoveredAlertPropertyID = null;
        state.hoveredAlertTargetCellKey = null;
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredRequiredFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
        state.alertsPanelAutoOpenedForNavBlock = false;
      }

      state.referenceViewOpen = action.payload;
    },
    clearAlertsPanelAutoOpenedForNavBlock(state) {
      state.alertsPanelAutoOpenedForNavBlock = false;
    },
    setMobileReferenceMenuOpen(state, action: PayloadAction<boolean>) {
      state.mobileReferenceMenuOpen = action.payload;
    },
    setProperties(state, action: PayloadAction<Tkv2ReferenceBarProperty[]>) {
      state.properties = action.payload;
    },
    setSelectedDataSheet(state, action: PayloadAction<DataSheet | null>) {
      if (action.payload !== 'craftsman-alerts') {
        state.focusedAlertType = null;
        state.focusedAlertPropertyID = null;
        state.focusedAlertTargetCellKey = null;
        state.hoveredAlertType = null;
        state.hoveredAlertPropertyID = null;
        state.hoveredAlertTargetCellKey = null;
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredRequiredFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
      state.selectedDataSheet = action.payload;
    },
    setFocusedAlertType(
      state,
      action: PayloadAction<AlertType | AlertFocusIdentity | null>,
    ) {
      const { payload } = action;
      if (payload === null) {
        state.focusedAlertType = null;
        state.focusedAlertPropertyID = null;
        state.focusedAlertTargetCellKey = null;
        return;
      }
      if (typeof payload === 'string') {
        state.focusedAlertType = payload;
        state.focusedAlertPropertyID = null;
        state.focusedAlertTargetCellKey = null;
        return;
      }
      state.focusedAlertType = payload.type;
      state.focusedAlertPropertyID = payload.propertyID ?? null;
      state.focusedAlertTargetCellKey = payload.targetCellKey ?? null;
    },
    setHoveredAlertType(
      state,
      action: PayloadAction<AlertType | AlertFocusIdentity | null>,
    ) {
      const { payload } = action;
      if (payload === null) {
        state.hoveredAlertType = null;
        state.hoveredAlertPropertyID = null;
        state.hoveredAlertTargetCellKey = null;
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredRequiredFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
        return;
      }
      if (typeof payload === 'string') {
        state.hoveredAlertType = payload;
        state.hoveredAlertPropertyID = null;
        state.hoveredAlertTargetCellKey = null;
        return;
      }
      state.hoveredAlertType = payload.type;
      state.hoveredAlertPropertyID = payload.propertyID ?? null;
      state.hoveredAlertTargetCellKey = payload.targetCellKey ?? null;
    },
    setHoveredPropertyGridCellKey(state, action: PayloadAction<PropertyGridCellKey | null>) {
      state.hoveredPropertyGridCellKey = action.payload;
      if (action.payload) {
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredRequiredFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredOrderDetailsFieldKey(state, action: PayloadAction<string | null>) {
      state.hoveredOrderDetailsFieldKey = action.payload;
      if (action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredRequiredFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredRequiredFieldKey(state, action: PayloadAction<string | null>) {
      state.hoveredRequiredFieldKey = action.payload;
      if (action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredCostApproachPropertyId(state, action: PayloadAction<string | null>) {
      state.hoveredCostApproachPropertyId = action.payload;
      if (action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredRequiredFieldKey = null;
      }
    },
    setActiveID(state, action: PayloadAction<ReferenceViewID>) {
      state.__doNotUseDirectly__ActiveID = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialReferenceBarUIState);
    builder.addCase(processAlertsForRoute, (state, action) => {
      if (action.payload.openAlertsPanel !== false) {
        state.alertsPanelAutoOpenedForNavBlock = true;
      }
    });
  },
});

export const {
  setReferenceViewOpen,
  setMobileReferenceMenuOpen,
  setProperties,
  setSelectedDataSheet,
  clearAlertsPanelAutoOpenedForNavBlock,
  setFocusedAlertType,
  setHoveredAlertType,
  setHoveredPropertyGridCellKey,
  setHoveredOrderDetailsFieldKey,
  setHoveredRequiredFieldKey,
  setHoveredCostApproachPropertyId,
  setActiveID,
} = referenceBarUISlice.actions;

export const referenceBarUIReducer = referenceBarUISlice.reducer;
